
import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import config from '../../config';
import { Calendar } from 'primereact/calendar';
import { Chips } from 'primereact/chips';
import JoditEditor from 'jodit-react';

function UpdateBlogDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState({
        title: '',
        description: '',
        category: ''
    });
    const [featuredImage, setFeaturedImage] = useState(null);
    const [removeImages, setRemoveImages] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const featuredImageRef = useRef(null);
    const editorRef = useRef(null);

    const [selectedOption, setSelectedOption] = useState("");
    const [contentData, setContentData] = useState([]);

    useEffect(() => {
        if (selectedOption === "imageText") {
            setContentData((prevData) => [...prevData, { type: selectedOption, contentImage: null, contentEditor: "", added: false }]);
        } else if (selectedOption === "editorOnly") {
            setContentData((prevData) => [...prevData, { type: selectedOption, contentEditor: "", added: false }]);
        } else if (selectedOption === "galleryImages") {
            setContentData((prevData) => [...prevData, { type: selectedOption, contentGalleryImages: null, added: false }]);
        } else if (selectedOption === "bannerImage") {
            setContentData((prevData) => [...prevData, { type: selectedOption, contentBannerImage: null, bannerText: "", added: false }]);
        }
    }, [selectedOption]);

    const handleImageUpload = async (base64Image, name) => {
        const formData = new FormData();
        formData.append('imageBase64', base64Image);
        formData.append('name', name);

        try {
            const response = await axios.post(`${config.BASE_URL}/api/admin/upload-image`, formData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response.data.imageUrl;
        } catch (error) {
            console.error('Image upload failed:', error);
            return null;
        }
    };

    const handleRepeaterInputChange = (index, e, isEditor = false) => {
        const updatedContent = [...contentData];

        if (isEditor) {
            updatedContent[index] = {
                ...updatedContent[index],
                contentEditor: e,
            };
            setContentData(updatedContent);
        } else {
            const { name, value, files } = e.target;

            if (files && files.length > 0) {
                const filePromises = Array.from(files).map((file) => {
                    return new Promise(async (resolve, reject) => {
                        const reader = new FileReader();

                        reader.onloadend = async () => {
                            const base64File = reader.result;

                            try {
                                const imageName = await handleImageUpload(base64File, name);
                                resolve(imageName);
                            } catch (error) {
                                reject(error);
                            }
                        };

                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                });

                Promise.all(filePromises)
                    .then((imageNames) => {
                        updatedContent[index] = {
                            ...updatedContent[index],
                            [name]: imageNames,
                        };
                        setContentData(updatedContent);
                    })
                    .catch((error) => {
                        console.error("Error uploading files:", error);
                        alert("There was an error uploading the image(s).");
                    });
            } else {
                updatedContent[index] = {
                    ...updatedContent[index],
                    [name]: value,
                };
                setContentData(updatedContent);
            }
        }
    };

    const removeRepeaterItem = (index) => {
        setContentData((prevData) => prevData.filter((_, i) => i !== index));
    };

    const addRepeaterData = () => {
        if (selectedOption) {
            const updatedContent = [...contentData];
            updatedContent[contentData.length - 1] = {
                ...updatedContent[contentData.length - 1],
                added: true,
            };

            setContentData(updatedContent);
            setSelectedOption("");
        }
    };

    useEffect(() => {
        const fetchBlogDetails = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/api/admin/get-blog/${id}`, { withCredentials: true });
                setBlog(response?.data);
                setContentData(response?.data?.contentSections || []);
            } catch (err) {
                setError('Failed to fetch blog details');
            }
        };

        fetchBlogDetails();
    }, [id]);

    const handleRemoveImage = async (imageName) => {
        try {
            const response = await axios.post(`${config.BASE_URL}/api/admin/remove-image-blog`, {
                blogId: blog._id,
                imageName
            }, { withCredentials: true });

            if (response.status === 200) {
                setRemoveImages(prev => [...prev, imageName]);
                setBlog(prevBlog => ({
                    ...prevBlog,
                    featuredImage: prevBlog.featuredImage === imageName ? null : prevBlog.featuredImage,
                }));
            } else {
                console.error('Failed to remove image');
            }
        } catch (err) {
            console.error('Error removing image:', err);
        }
    };

    const handleFeaturedImageChange = (e) => {
        setFeaturedImage(e.target.files[0]);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('title', blog.title);
        formData.append('description', blog.description);
        formData.append('category', blog.category);
        formData.append('contentData', JSON.stringify(contentData));

        if (featuredImage) {
            formData.append('featuredImage', featuredImage);
        }

        try {
            const response = await axios.put(`${config.BASE_URL}/api/admin/update-blog/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true,
            });

            setBlog(response.data.blog);
            if (featuredImageRef.current) {
                featuredImageRef.current.value = '';
            }
            setFeaturedImage(null);
            setSuccessMessage('Blog updated successfully!');
        } catch (err) {
            setError('Failed to update blog');
        }
    };

    const handleEditorChange = (index, newValue) => {
        setContentData(prevContentData => {
            const updatedContent = [...prevContentData];
            updatedContent[index] = {
                ...updatedContent[index],
                contentEditor: newValue,
            };
            return updatedContent;
        });
    };

    const editorConfig = useMemo(() => ({
        height: 400,
        toolbarAdaptive: false,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        buttons: [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'ul',
            'ol',
            '|',
            'center',
            'left',
            'right',
            'justify',
            '|',
            'link',
            'undo',
            'redo',
            '|',
            'source', '|',
            'outdent', 'indent', 'font', 'fontsize', 'brush', 'paragraph', '|',
        ],
        placeholder: 'Start typing your content here...',
        style: {
            color: '#000000',
            background: '#ffffff',
            fontSize: '16px',
        },
        limitChars: 10000,
        autofocus: true,
    }), []);

    return (
        <div className="dashboard update_detail_wrap">
            <div className="container">
                <h1 className="dash_title">Edit Blog</h1>
                <div className="back_btn_wrap">
                    <button className="back-btn" onClick={() => navigate(-1)}>Go Back</button>
                </div>
                {blog ? (
                    <form onSubmit={handleFormSubmit} encType="multipart/form-data">
                        <div className="update_details_wrap">
                            <div className="form-group">
                                <label>Blog Title</label>
                                <input
                                    type="text"
                                    value={blog.title}
                                    onChange={(e) => setBlog({ ...blog, title: e.target.value })}
                                />
                            </div>

                            <div className="form-group textareaField">
                                <h2>Description</h2>
                                <textarea
                                    name="description"
                                    value={blog.description}
                                    onChange={(e) => setBlog({ ...blog, description: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Category</label>
                                <input
                                    type="text"
                                    value={blog.category}
                                    onChange={(e) => setBlog({ ...blog, category: e.target.value })}
                                />
                            </div>

                            <div className="add_field repeaterFieldsData titleField">
                                <h2>Blog Content</h2>
                                {contentData.length > 0 && contentData.map((item, index) => {
                                    if (item.added === true) {
                                        if (item.type === "imageText") {
                                            return (
                                                <>
                                                    <div className="singleReapterField" key={index}>
                                                        <div>
                                                            <h3>Image and Text</h3>
                                                            <p>Current Image: {item.contentImage && item.contentImage[0] && (
                                                                <img src={config.BASE_URL + item.contentImage[0]} alt="Content" style={{ maxWidth: '200px' }} />
                                                            )}</p>
                                                            <span className="mt-10 mb-15 ">
                                                                
                                                                <input
                                                                    type="file"
                                                                    name="contentImage"
                                                                    onChange={(e) => handleRepeaterInputChange(index, e)}
                                                                />
                                                            </span>
                                                            <h4>Text</h4>
                                                        </div>
                                                    </div>
                                                    <JoditEditor
                                                        ref={editorRef}
                                                        value={item.contentEditor}
                                                        config={editorConfig}
                                                        onChange={(newContent) => handleRepeaterInputChange(index, newContent, true)}
                                                    />
                                                    <div className="mt-10 mb-15">
                                                        <span className="btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                                                    </div>
                                                </>
                                            );
                                        }

                                        if (item.type === "editorOnly") {
                                            return (
                                                <>
                                                    <div className="singleReapterField" key={index}>
                                                        <div>
                                                            <h3>Editor</h3>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <JoditEditor
                                                            ref={editorRef}
                                                            value={item.contentEditor}
                                                            config={editorConfig}
                                                            onChange={(newContent) => handleEditorChange(index, newContent)}
                                                        />
                                                        <div className="mt-10 mb-15">
                                                            <span className="btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        }

                                        if (item.type === "galleryImages") {
                                            return (
                                                <div className="singleReapterField2" key={index}>
                                                    <div>
                                                        <h3>Gallery Images</h3>
                                                        <p>Current Images:</p>
                                                        {item.contentGalleryImages && item.contentGalleryImages.length > 0 && item.contentGalleryImages.map((img, imgIndex) => (
                                                            <div key={imgIndex}>
                                                                <img src={config.BASE_URL + img} alt="Gallery" style={{ maxWidth: '200px', margin: '5px' }} />
                                                            </div>
                                                        ))}
                                                        <input
                                                            type="file"
                                                            name="contentGalleryImages"
                                                            onChange={(e) => handleRepeaterInputChange(index, e)}
                                                            multiple
                                                        />
                                                        <div className="mt-10 mb-15 -ml-10">
                                                            <span className="btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (item.type === "bannerImage") {
                                            return (
                                                <div className="singleReapterField2" key={index}>
                                                    <div>
                                                        <h3>Banner Image</h3>
                                                        <p>Current Image: {item.contentBannerImage && item.contentBannerImage[0] && (
                                                            <img src={config.BASE_URL + item.contentBannerImage[0]} alt="Banner" style={{ maxWidth: '200px' }} />
                                                        )}</p>
                                                        <input className="mt-5"
                                                            type="file"
                                                            name="contentBannerImage"
                                                            onChange={(e) => handleRepeaterInputChange(index, e)}
                                                        />
                                                        <h4>Banner Text</h4>
                                                        <input
                                                            type="text"
                                                            name="bannerText"
                                                            value={item.bannerText}
                                                            onChange={(e) => handleRepeaterInputChange(index, e)}
                                                        />
                                                        <div className="mt-10 mb-15 -ml-10">
                                                            <span className="btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    }
                                    return null;
                                })}
                            </div>

                            <div className="add_field mb-10">
                                <h2>Select Option</h2>
                                <select
                                    value={selectedOption}
                                    onChange={(e) => setSelectedOption(e.target.value)}
                                >
                                    <option value="">--Select an Option--</option>
                                    <option value="imageText">Image + Text</option>
                                    <option value="editorOnly">Editor Only</option>
                                    <option value="galleryImages">Gallery Images</option>
                                    <option value="bannerImage">Banner Image</option>
                                </select>
                            </div>

                            {selectedOption === "imageText" && contentData.length > 0 && (
                                <div className="add_field">
                                    <h2>Image</h2>
                                    <input
                                        type="file"
                                        name="contentImage"
                                        onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                                        required
                                    />
                                    <h2>Editor</h2>
                                    <JoditEditor
                                        required
                                        ref={editorRef}
                                        value={contentData[contentData.length - 1].contentEditor}
                                        config={editorConfig}
                                        onChange={(newContent) => handleRepeaterInputChange(contentData.length - 1, newContent, true)}
                                    />
                                </div>
                            )}

                            {selectedOption === "editorOnly" && contentData.length > 0 && (
                                <div className="add_field">
                                    <h2>Editor</h2>
                                    <div style={{ border: '1px solid #ccc', minHeight: '400px', background: '#fff', color: '#000' }}>
                                        <JoditEditor
                                            ref={editorRef}
                                            value={contentData[contentData.length - 1].contentEditor}
                                            config={editorConfig}
                                            onChange={(newContent) => handleEditorChange(contentData.length - 1, newContent)}
                                        />
                                    </div>
                                </div>
                            )}

                            {selectedOption === "galleryImages" && contentData.length > 0 && (
                                <div className="add_field">
                                    <h2>Gallery Images</h2>
                                    <input
                                        type="file"
                                        name="contentGalleryImages"
                                        onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                                        multiple
                                        required
                                    />
                                </div>
                            )}

                            {selectedOption === "bannerImage" && contentData.length > 0 && (
                                <div className="add_field">
                                    <h2>Banner Image</h2>
                                    <input
                                        type="text"
                                        name="bannerText"
                                        value={contentData[contentData.length - 1].bannerText}
                                        onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                                        required
                                    />
                                    <input
                                        type="file"
                                        name="contentBannerImage"
                                        onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                                        required
                                    />
                                </div>
                            )}

                            {selectedOption !== "" && (
                                <div className="mt-10 mb-15">
                                    <span onClick={addRepeaterData} style={{ background: "#fff", color: "#000", padding: "8px 30px", cursor: "pointer" }}>Add</span>
                                </div>
                            )}

                            <div className="form-group">
                                <div className="detail_banner_wrap">
                                    <div className="detail_img_grid">
                                        <div className="upload_item">
                                            <label>Featured Image</label>
                                            <input type="file" onChange={handleFeaturedImageChange} ref={featuredImageRef} />
                                        </div>
                                        <div className="detail_img_grid">
                                            {blog.featuredImage ? (
                                                <div className="detail_img_item">
                                                    <div className="banner_img_wrap">
                                                        <img src={`${config.BASE_URL}${blog.featuredImage}`} alt="Featured" />
                                                    </div>
                                                    <div className="mt-10 mb-15">
                                                        <button className="remove_btn" type="button" onClick={() => handleRemoveImage(blog.featuredImage)}>Remove</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p>No image available.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="update_btn_wrap">
                                <button className="update_btn" type="submit">Update Blog</button>
                            </div>
                            {successMessage && <p className="success-message">{successMessage}</p>}
                            {error && <p className="error-message">{error}</p>}
                        </div>
                    </form>
                ) : (
                    <h5>Blog not found!</h5>
                )}
            </div>
        </div>
    );
}

export default UpdateBlogDetail;